import { useRef } from 'react';
import * as THREE from 'three';
import { useSlotTheme } from '../../hooks/useSlotTheme';
import type { LightConfig } from '../../themes/types';

/**
 * Renders lights based on theme configuration
 */
export function Lights() {
  const { theme } = useSlotTheme();
  const lights = theme.cabinet.lights ?? [];

  return (
    <>
      {lights.map((light, index) => (
        <ThemeLight key={`${light.type}-${index}`} config={light} />
      ))}
    </>
  );
}

interface ThemeLightProps {
  config: LightConfig;
}

function ThemeLight({ config }: ThemeLightProps) {
  const lightRef = useRef<THREE.Light>(null);

  switch (config.type) {
    case 'ambient':
      return <ambientLight color={config.color} intensity={config.intensity} />;

    case 'directional':
      return (
        <directionalLight
          ref={lightRef}
          color={config.color}
          intensity={config.intensity}
          position={config.position ?? [0, 10, 0]}
          castShadow
          shadow-mapSize={[1024, 1024]}
          shadow-camera-far={50}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
        />
      );

    case 'point':
      return (
        <pointLight
          ref={lightRef}
          color={config.color}
          intensity={config.intensity}
          position={config.position ?? [0, 5, 0]}
          castShadow
          shadow-mapSize={[512, 512]}
        />
      );

    case 'spot':
      return (
        <spotLight
          ref={lightRef}
          color={config.color}
          intensity={config.intensity}
          position={config.position ?? [0, 10, 0]}
          angle={config.angle ?? Math.PI / 4}
          penumbra={config.penumbra ?? 0.5}
          castShadow
          shadow-mapSize={[512, 512]}
        />
      );

    default:
      return null;
  }
}

/**
 * Default fallback lights if theme doesn't specify any
 */
export function DefaultLights() {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight
        position={[5, 10, 5]}
        intensity={1}
        castShadow
        shadow-mapSize={[1024, 1024]}
      />
      <pointLight position={[-5, 5, 3]} intensity={0.5} color="#FFD700" />
    </>
  );
}
